// ExternalLinksSection.jsx
import React, { useEffect, useRef, useState } from "react";
import { useAuthFetch } from "../hooks/useAuthFetch";

export default function ExternalLinksSection({ projectId }) {
  const { authFetch, isReady } = useAuthFetch();
  const [isExpanded, setIsExpanded] = useState(false);

  // one link each
  const [projectManagement, setProjectManagement] = useState(null); // {name,url}
  const [billing, setBilling] = useState(null);                     // {name,url}

  // many links
  const [extraLinks, setExtraLinks] = useState([]);                 // [{name,url}, ...]

  // add/edit dialog
  const [modal, setModal] = useState({
    open: false,
    group: null,          // 'pms' | 'billing' | 'additional'
    editIndex: null,      // only for 'additional'
    siteName: "",
    siteUrl: "",
    title: "Additional External Link",
  });

  // context menu (3-dots)
  const [menu, setMenu] = useState({ open: false, group: null, index: null });
  const menuRef = useRef(null);

  // track if initial fetch done (to fetch on first expand only)
  const hasFetchedRef = useRef(false);

  // ===== BASE URL (no trailing slash) =====
  const BASE =
    (process.env.REACT_APP_BACKEND_BASEURL || "http://localhost:3000").replace(/\/+$/, "");

  // ===== utilities =====
  const normalizeLink = (obj) => {
    if (!obj) return null;
    const name = obj.name ?? obj.site_name ?? "";
    const url  = obj.url  ?? obj.website_url ?? "";
    if (!name && !url) return null;
    return { name, url };
  };

  const toServerLink = (link) =>
    link ? { site_name: link.name, website_url: link.url } : null;

  // project_number is exactly projectId
  const projectNumber = String(projectId ?? "").trim();

  // close context menu on outside click/escape
  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenu({ open: false, group: null, index: null });
      }
    };
    const onKey = (e) => e.key === "Escape" && setMenu({ open: false, group: null, index: null });
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const openInNewTab = (url) => {
    if (!url) return;
    const normalized = /^(http|https):\/\//i.test(url) ? url : `https://${url}`;
    window.open(normalized, "_blank");
  };

  const openDialog = (group, initial = null, idx = null) => {
    setModal({
      open: true,
      group,
      editIndex: idx,
      siteName: initial?.name || "",
      siteUrl: initial?.url || "",
      title: "Additional External Link", // keep same heading to match app dialog
    });
  };

  const closeDialog = () =>
    setModal((m) => ({ ...m, open: false, siteName: "", siteUrl: "", editIndex: null }));

  // ===== API: GET existing links =====
  const fetchLinks = async () => {
    if (!projectNumber || !isReady) return;
    try {
      const res = await authFetch(
        `${BASE}/api/external-links/project/${encodeURIComponent(projectNumber)}`,
        { method: "GET", headers: { Accept: "application/json" } }
      );
      if (!res.ok) throw new Error(`GET failed: ${res.status}`);
      const json = await res.json();

      // The backend returns: { status, message, data: { project_number, links: [...] } }
      const payload = json?.data ?? json;
      let record = null;

      if (Array.isArray(payload?.links)) {
        // choose the latest non-deleted record by updated_at (fallback created_at)
        const candidates = payload.links.filter((l) => !l.is_deleted);
        candidates.sort((a, b) => {
          const ta = new Date(a.updated_at || a.created_at || 0).getTime();
          const tb = new Date(b.updated_at || b.created_at || 0).getTime();
          return tb - ta;
        });
        record = candidates[0] || null;
      } else if (payload && (payload.project_site || payload.billing_site || payload.additional_site)) {
        // tolerant fallback if API ever returns a single object
        record = payload;
      }

      setProjectManagement(normalizeLink(record?.project_site));
      setBilling(normalizeLink(record?.billing_site));

      // additional_site is expected to be { sites: [...] }
      const rawSection = record?.additional_site;
      let arr = [];
      if (Array.isArray(rawSection?.sites)) arr = rawSection.sites;
      else if (Array.isArray(rawSection)) arr = rawSection;  // tolerant fallback
      else if (rawSection) arr = [rawSection];               // tolerant fallback

      const cleaned = arr.map(normalizeLink).filter(Boolean);

      // de-duplicate by name+url
      const seen = new Set();
      const unique = cleaned.filter((l) => {
        const k = `${l.name}::${l.url}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      setExtraLinks(unique);
    } catch (e) {
      console.error("ExternalLinksSection: fetch error:", e);
    }
  };

  // Fetch on first expand only
  useEffect(() => {
    if (isExpanded && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchLinks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  // ===== API: POST upsert (additional_site as { sites: [...] }) =====
  const postUpsert = async (nextPMS, nextBilling, nextExtras) => {
    if (!projectNumber) return;

    const payload = {
      project_number: projectNumber,
      project_site: toServerLink(nextPMS),
      billing_site: toServerLink(nextBilling),
      additional_site: {
        sites: Array.isArray(nextExtras)
          ? nextExtras.map(toServerLink).filter(Boolean)
          : [],
      },
    };

    try {
      const res = await authFetch(`${BASE}/api/external-links`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`POST failed: ${res.status}`);
    } catch (e) {
      console.error("ExternalLinksSection: upsert error:", e);
    }
  };

  // unified apply-change that updates local state and syncs to server
  const applyChange = (group, linkOrNull, index = null) => {
    if (group === "pms") {
      const next = linkOrNull ? { ...linkOrNull } : null;
      setProjectManagement(next);
      postUpsert(next, billing, extraLinks);
      return;
    }
    if (group === "billing") {
      const next = linkOrNull ? { ...linkOrNull } : null;
      setBilling(next);
      postUpsert(projectManagement, next, extraLinks);
      return;
    }
    // additional
    if (group === "additional") {
      let nextExtras;
      if (index != null) {
        // edit or delete specific index
        if (linkOrNull) {
          nextExtras = extraLinks.map((l, i) => (i === index ? { ...linkOrNull } : l));
        } else {
          nextExtras = extraLinks.filter((_, i) => i !== index);
        }
      } else {
        // add new
        nextExtras = [...extraLinks, { ...linkOrNull }];
      }
      setExtraLinks(nextExtras);
      postUpsert(projectManagement, billing, nextExtras);
    }
  };

  const saveFromDialog = () => {
    const name = modal.siteName.trim();
    const url = modal.siteUrl.trim();
    if (!name || !url) return;
    const link = { name, url };

    if (modal.group === "pms") {
      applyChange("pms", link);
    } else if (modal.group === "billing") {
      applyChange("billing", link);
    } else if (modal.group === "additional") {
      if (modal.editIndex != null) {
        applyChange("additional", link, modal.editIndex);
      } else {
        applyChange("additional", link, null);
      }
    }
    closeDialog();
  };

  const deletePMS = () => applyChange("pms", null);
  const deleteBilling = () => applyChange("billing", null);
  const deleteAdditional = (i) => applyChange("additional", null, i);

  // helpers to toggle context menu
  const showMenu = (group, index = null) => {
    setMenu((m) =>
      m.open && m.group === group && m.index === index
        ? { open: false, group: null, index: null }
        : { open: true, group, index }
    );
  };

  return (
    <>
      {/* Collapsible header styled like Nettles Links */}
      <div className={`menu-item-wrapper ${isExpanded ? "active" : ""}`}>
        <div className={`indicator ${isExpanded ? "active" : ""}`} />
        <div
          className="menu-item"
          onClick={() => setIsExpanded((x) => !x)}
          style={{ cursor: "pointer" }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M10 18c4.418 0 8-3.582 8-8s-3.582-8-8-8S2 5.582 2 10s3.582 8 8 8Z"
              stroke={isExpanded ? "#DC2626" : "#374151"}
              strokeWidth="2"
            />
            <path
              d="M2 10h16M10 2c2.5 2.74 3.92 5.29 4 8-.08 2.71-1.5 5.26-4 8-2.5-2.74-3.92-5.29-4-8 .08-2.71 1.5-5.26 4-8Z"
              stroke={isExpanded ? "#DC2626" : "#374151"}
              strokeWidth="2"
            />
          </svg>
          <span className="menu-text">External Links</span>
          <svg
            className={`dropdown-icon ${isExpanded ? "rotate-180" : ""}`}
            width="20"
            height="20"
            viewBox="0 0 20 20" fill="none"
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke={isExpanded ? "#DC2626" : "#374151"}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {isExpanded && (
        <div className="contacts-content">

          {/* ===== Project Management Site (header + item) ===== */}
          <div className="flex justify-between items-center mb-1">
            <span className="font-semibold text-gray-700 text-sm">
              Project Management Site
            </span>
            {!projectManagement && (
              <button
                className="bg-red-600 text-white rounded w-5 h-5 text-[12px] leading-none flex items-center justify-center"
                onClick={() => openDialog("pms", projectManagement)}
                title="Add"
              >
                +
              </button>
            )}
          </div>

          {projectManagement ? (
            <div
              className="contact-tab-item ml-4 relative"
              onClick={() => openInNewTab(projectManagement.url)}
              style={{ cursor: "pointer" }}
            >
              <div className="indicator"></div>
              <div className="tab-content flex justify-between items-center w-full">
                <span className="text-[13px] font-semibold">{projectManagement.name}</span>
                <button
                  className="text-gray-400 hover:text-gray-600 px-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    showMenu("pms");
                  }}
                  aria-label="Menu"
                >
                  ⋮
                </button>
              </div>

              {menu.open && menu.group === "pms" && (
                <div ref={menuRef} className="absolute right-1 top-7 z-20 bg-white border rounded shadow-sm">
                  <button
                    className="block w-full text-left text-xs px-3 py-2 hover:bg-gray-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenu({ open: false, group: null, index: null });
                      openDialog("pms", projectManagement);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="block w-full text-left text-xs px-3 py-2 text-red-600 hover:bg-gray-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      deletePMS();
                      setMenu({ open: false, group: null, index: null });
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="ml-4 text-[13px] text-gray-500 italic mb-2">No link set</div>
          )}

          {/* ===== Billing site (header + item) ===== */}
          <div className="flex justify-between items-center mt-2 mb-1">
            <span className="font-semibold text-gray-700 text-sm">Billing Site</span>
            {!billing && (
              <button
                className="bg-red-600 text-white rounded w-5 h-5 text-[12px] leading-none flex items-center justify-center"
                onClick={() => openDialog("billing", billing)}
                title="Add"
              >
                +
              </button>
            )}
          </div>

          {billing ? (
            <div
              className="contact-tab-item ml-4 relative"
              onClick={() => openInNewTab(billing.url)}
              style={{ cursor: "pointer" }}
            >
              <div className="indicator"></div>
              <div className="tab-content flex justify-between items-center w-full">
                <span className="text-[13px] font-semibold">{billing.name}</span>
                <button
                  className="text-gray-400 hover:text-gray-600 px-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    showMenu("billing");
                  }}
                  aria-label="Menu"
                >
                  ⋮
                </button>
              </div>

              {menu.open && menu.group === "billing" && (
                <div ref={menuRef} className="absolute right-1 top-7 z-20 bg-white border rounded shadow-sm">
                  <button
                    className="block w-full text-left text-xs px-3 py-2 hover:bg-gray-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenu({ open: false, group: null, index: null });
                      openDialog("billing", billing);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="block w-full text-left text-xs px-3 py-2 text-red-600 hover:bg-gray-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteBilling();
                      setMenu({ open: false, group: null, index: null });
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="ml-4 text-[13px] text-gray-500 italic mb-2">No link set</div>
          )}

          {/* ===== Additional External Links ===== */}
          <div className="flex justify-between items-center mt-2 mb-1">
            <span className="font-semibold text-gray-700 text-sm">Additional External Links</span>
            <button
              className="bg-red-600 text-white rounded w-5 h-5 text-[12px] leading-none flex items-center justify-center"
              onClick={() => openDialog("additional")}
              title="Add"
            >
              +
            </button>
          </div>

          {extraLinks.length === 0 && (
            <div className="ml-4 text-[13px] text-gray-500 italic">link to an additional site</div>
          )}

          {extraLinks.map((link, i) => (
            <div
              key={`${link.name}-${i}`}
              className="contact-tab-item ml-4 relative"
              onClick={() => openInNewTab(link.url)}
              style={{ cursor: "pointer" }}
            >
              <div className="indicator"></div>
              <div className="tab-content flex justify-between items-center w-full">
                <span className="text-[13px]">{link.name}</span>
                <button
                  className="text-gray-400 hover:text-gray-600 px-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    showMenu("additional", i);
                  }}
                  aria-label="Menu"
                >
                  ⋮
                </button>
              </div>

              {menu.open && menu.group === "additional" && menu.index === i && (
                <div ref={menuRef} className="absolute right-1 top-7 z-20 bg-white border rounded shadow-sm">
                  <button
                    className="block w-full text-left text-xs px-3 py-2 hover:bg-gray-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenu({ open: false, group: null, index: null });
                      openDialog("additional", link, i);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="block w-full text-left text-xs px-3 py-2 text-red-600 hover:bg-gray-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteAdditional(i);
                      setMenu({ open: false, group: null, index: null });
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ===== Dialog (same feel as other modals in app) ===== */}
      {modal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-6 w-[680px] max-w-[94vw]">
            <h3 className="text-lg font-semibold mb-4">{modal.title}</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Site Name:
                </label>
                <input
                  type="text"
                  className="border w-full rounded px-3 py-2 text-sm"
                  value={modal.siteName}
                  onChange={(e) => setModal((m) => ({ ...m, siteName: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Website URL:
                </label>
                <input
                  type="text"
                  placeholder="https://example.com"
                  className="border w-full rounded px-3 py-2 text-sm"
                  value={modal.siteUrl}
                  onChange={(e) => setModal((m) => ({ ...m, siteUrl: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={closeDialog} className="px-4 py-2 border rounded text-sm">
                Cancel
              </button>
              <button
                onClick={saveFromDialog}
                className="px-5 py-2 bg-red-600 text-white rounded text-sm"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
